'use client';

import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { SortAscending, Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useCatalogViewStore, type CatalogSortMode } from '@/stores/catalog-view-store';

const SORT_OPTIONS: { value: CatalogSortMode; label: string }[] = [
  { value: 'default', label: 'По умолчанию' },
  { value: 'name-asc', label: 'По названию А–Я' },
  { value: 'name-desc', label: 'По названию Я–А' },
  { value: 'price-asc', label: 'Сначала дешёвые' },
  { value: 'price-desc', label: 'Сначала дорогие' },
];

export function SortButton({ className }: { className?: string }) {
  const sortMode = useCatalogViewStore((s) => s.sortMode);
  const setSortMode = useCatalogViewStore((s) => s.setSortMode);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  const isActive = sortMode !== 'default';

  return (
    <div ref={ref} className={cn('relative md:hidden shrink-0', className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center justify-center',
          'w-8 h-8 rounded-pill',
          'transition-colors duration-150',
          isActive
            ? 'bg-[var(--primary)] text-white shadow-glow'
            : 'bg-[var(--surface-secondary)] dark:bg-white/5 text-[var(--text-tertiary)]'
        )}
        aria-label="Сортировка"
        aria-expanded={open}
      >
        <SortAscending className="h-4 w-4" weight={isActive ? 'fill' : 'regular'} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 z-50',
            'min-w-[200px] py-1.5',
            // Тёмная тема: подложка списка на ступень светлее поверхности.
            // Запасное значение после запятой сейчас не срабатывает —
            // переменная приподнятой поверхности заведена в обеих темах
            // (проверено сборкой стилей). Оставлено как страховка на случай,
            // если переменную из палитры уберут: без неё список слился бы
            // с фоном, а не пропал.
            'bg-[var(--surface)] dark:bg-[var(--surface-elevated,var(--surface))]',
            'border border-[var(--border-default)]',
            // overflow-hidden обязателен вместе с card: пункты списка тянут
            // подсветку нажатия во всю ширину и упираются в углы. На радиусе
            // 16 угол рубил ~3,5 px подсветки, на 20 — почти 6, и серый
            // прямоугольник вылезал за скругление. Клип, а не подгонка
            // отступов: отступы двигают вертикаль всего списка.
            'rounded-card overflow-hidden shadow-float',
            'animate-in fade-in slide-in-from-top-2 duration-150'
          )}
        >
          {SORT_OPTIONS.map(({ value, label }) => {
            const selected = sortMode === value;
            return (
              <button
                key={value}
                onClick={() => {
                  setSortMode(value);
                  setOpen(false);
                }}
                className={cn(
                  'flex items-center gap-2.5 w-full px-4 py-2.5',
                  'text-sm text-left transition-colors',
                  selected
                    ? 'text-[var(--primary)] font-medium'
                    : 'text-[var(--text-primary)] active:bg-[var(--surface-secondary)]'
                )}
              >
                <span className="flex-1">{label}</span>
                {selected && <Check className="h-4 w-4 shrink-0" weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
